/**
 * Data Operations card: Data Health & Execution Integrity (read-only).
 *
 * Shows, for a date range and optional strategy:
 *   - 09:15 candle open vs the official day open (per day)
 *   - option-chain / spot feed freshness
 *   - pre-trade integrity gate results (shadow)
 *   - execution evidence (intended vs recorded price, price source)
 *   - broker reconciliation events (shadow)
 *
 * Truthfulness rule: ONLY a fully passing result is green. UNKNOWN,
 * NOT_CHECKED, NOT_RECORDED and "no data" are never shown as healthy.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import executionIntegrityService, {
  type ExecutionIntegritySummary,
  type IntegrityParams,
  type IntegrityRow,
  type IntegritySection,
  type SectionHealth,
} from "@/services/executionIntegrityService";
import { extractErrorMessage } from "@/utils/errors";

type ChipColor = "success" | "error" | "warning" | "info" | "default";

/** Only these are ever green. */
const GREEN = new Set(["HEALTHY", "MATCH", "FRESH", "PASS", "CONFIRMED", "IN_SYNC"]);
const RED = new Set([
  "ATTENTION", "MISMATCH", "STALE", "WOULD_BLOCK", "BLOCKED", "ERROR", "FAIL", "PRICE_INFERRED", "CRITICAL",
  "REVERSED_AT_BROKER", "CLOSED_AT_BROKER", "NO_BROKER_ROW", "PARTIAL_AT_BROKER", "EXCESS_AT_BROKER",
  "REDUCE_TO_HELD", "SKIP_ALREADY_FLAT", "BLOCK_REVERSED",
]);
const BLUE = new Set(["SIMULATED", "OPEN"]);

export function statusColor(status: unknown): ChipColor {
  const s = String(status ?? "");
  if (GREEN.has(s)) return "success";
  if (RED.has(s)) return "error";
  if (BLUE.has(s)) return "info";
  if (s === "UNKNOWN" || s === "NOT_CHECKED" || s === "NOT_RECORDED" || s === "WARNING") return "warning";
  return "default";
}

export function statusLabel(status: unknown): string {
  const s = String(status ?? "—");
  if (s === "NO_DATA") return "No data";
  if (s === "NOT_CHECKED") return "Not checked";
  if (s === "NOT_RECORDED") return "Not recorded";
  return s.replace(/_/g, " ");
}

function StatusChip({ status }: { status: unknown }) {
  if (status === null || status === undefined || status === "") return <>—</>;
  return <Chip size="small" label={statusLabel(status)} color={statusColor(status)} />;
}

interface Column {
  key: string;
  label: string;
  status?: boolean;
  time?: boolean;
}

const SECTIONS: { key: IntegritySection; summaryKey: keyof ExecutionIntegritySummary; label: string; columns: Column[] }[] = [
  {
    key: "first-candle", summaryKey: "first_candle", label: "09:15 open check",
    columns: [
      { key: "date", label: "Date" }, { key: "ws_open", label: "WS open" }, { key: "official_open", label: "Official open" },
      { key: "diff", label: "Diff" }, { key: "close", label: "Close" },
      { key: "direction_flip", label: "Direction differs" }, { key: "status", label: "Status", status: true },
      { key: "note", label: "Note" },
    ],
  },
  {
    key: "feed-freshness", summaryKey: "feed_freshness", label: "Feed freshness",
    columns: [
      { key: "symbol_code", label: "Symbol" }, { key: "feed", label: "Feed" },
      { key: "latest_capture", label: "Latest capture", time: true }, { key: "age_seconds", label: "Age (s)" },
      { key: "status", label: "Status", status: true }, { key: "note", label: "Note" },
    ],
  },
  {
    key: "gate", summaryKey: "gate", label: "Integrity gate",
    columns: [
      { key: "candle_time", label: "Candle", time: true }, { key: "strategy_key", label: "Strategy" },
      { key: "option_type", label: "Side" }, { key: "mode", label: "Mode" },
      { key: "result", label: "Result", status: true }, { key: "blocking_checks", label: "Blocking checks" },
      { key: "unknown_checks", label: "Unknown checks" }, { key: "summary", label: "Summary" },
    ],
  },
  {
    key: "evidence", summaryKey: "evidence", label: "Execution evidence",
    columns: [
      { key: "entry_time", label: "Entry", time: true }, { key: "strategy_key", label: "Strategy" },
      { key: "mode", label: "Mode" }, { key: "leg_role", label: "Leg" }, { key: "contract", label: "Contract" },
      { key: "entry_intended", label: "Intended" }, { key: "entry_recorded", label: "Recorded" },
      { key: "entry_status", label: "Entry price", status: true }, { key: "exit_status", label: "Exit price", status: true },
      { key: "reduce_only_decision", label: "Reduce-only", status: true },
      { key: "status", label: "Overall", status: true },
    ],
  },
  {
    key: "reconciliation", summaryKey: "reconciliation", label: "Broker reconciliation",
    columns: [
      { key: "checked_at", label: "Checked", time: true }, { key: "strategy_key", label: "Strategy" },
      { key: "leg_role", label: "Leg" }, { key: "contract", label: "Contract" },
      { key: "expected_net", label: "Expected" }, { key: "broker_net", label: "Broker" },
      { key: "state", label: "State", status: true }, { key: "severity", label: "Severity", status: true },
      { key: "planned_action", label: "Planned action" }, { key: "action_taken", label: "Taken" },
      { key: "message", label: "Message" },
    ],
  },
];

const STRATEGIES = ["", "TEK_915_ITM_BUY", "TEK_915_CREDIT_SPREAD", "TEK_MB01", "TEK_RSI_MOMENTUM"];

function istToday(): string {
  const now = new Date(Date.now() + 330 * 60 * 1000);
  return now.toISOString().slice(0, 10);
}

function daysBefore(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmt(value: unknown, col: Column): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (col.time && typeof value === "string") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    }
  }
  return String(value);
}

function SummaryTile({ label, health, onClick, active }: { label: string; health?: SectionHealth; onClick: () => void; active: boolean }) {
  const counts = Object.entries(health?.counts ?? {}).map(([k, v]) => `${statusLabel(k)}: ${v}`).join(", ");
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{ p: 1.5, cursor: "pointer", height: "100%", borderColor: active ? "primary.main" : undefined, borderWidth: active ? 2 : 1 }}
      data-testid={`integrity-tile-${label}`}
    >
      <Typography variant="subtitle2">{label}</Typography>
      <Box sx={{ my: 0.5 }}>
        <StatusChip status={health?.status ?? "UNKNOWN"} />
      </Box>
      <Typography variant="caption" color="text.secondary">{counts || "nothing in range"}</Typography>
    </Paper>
  );
}

export function ExecutionIntegrityCard() {
  const [dateTo, setDateTo] = useState(istToday());
  const [dateFrom, setDateFrom] = useState(daysBefore(istToday(), 6));
  const [strategy, setStrategy] = useState("");
  const [section, setSection] = useState<IntegritySection>("first-candle");
  const [extra, setExtra] = useState<IntegrityParams>({});
  const [summary, setSummary] = useState<ExecutionIntegritySummary | null>(null);
  const [rows, setRows] = useState<IntegrityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo<IntegrityParams>(
    () => ({ date_from: dateFrom, date_to: dateTo, strategy_key: strategy || undefined, ...extra }),
    [dateFrom, dateTo, strategy, extra],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        executionIntegrityService.getSummary({ date_from: dateFrom, date_to: dateTo, strategy_key: strategy || undefined }),
        executionIntegrityService.getRows(section, params),
      ]);
      setSummary(s);
      setRows(r.rows);
    } catch (e) {
      setError(extractErrorMessage(e));
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, strategy, section, params]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = SECTIONS.find((s) => s.key === section) ?? SECTIONS[0];

  const changeSection = (key: IntegritySection) => {
    setExtra({});
    setSection(key);
  };

  const download = async () => {
    try {
      await executionIntegrityService.downloadCsv(section, params);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6">Data Health &amp; Execution Integrity</Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only. Only a fully passing result is green — Unknown, Not checked, Not recorded and No data are
              never shown as healthy. Gate and reconciliation run in shadow mode (they record, they do not act).
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
          <TextField label="From" type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} />
          <TextField select label="Strategy" size="small" value={strategy} onChange={(e) => setStrategy(e.target.value)}
            sx={{ minWidth: 220 }}>
            {STRATEGIES.map((s) => (
              <MenuItem key={s || "all"} value={s}>{s || "All strategies"}</MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {SECTIONS.map((s) => (
            <Grid item xs={12} sm={6} md key={s.key}>
              <SummaryTile label={s.label} active={s.key === section}
                health={summary ? (summary[s.summaryKey] as SectionHealth) : undefined} onClick={() => changeSection(s.key)} />
            </Grid>
          ))}
        </Grid>

        <Tabs value={section} onChange={(_, v) => changeSection(v)} variant="scrollable" sx={{ mb: 1 }}>
          {SECTIONS.map((s) => <Tab key={s.key} value={s.key} label={s.label} />)}
        </Tabs>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1 }} alignItems={{ sm: "center" }}>
          {section === "gate" && (
            <TextField select size="small" label="Result" value={extra.outcome ?? ""} sx={{ minWidth: 180 }}
              onChange={(e) => setExtra({ outcome: e.target.value || undefined })}>
              {["", "PASS", "WOULD_BLOCK", "BLOCKED", "ERROR"].map((o) => (
                <MenuItem key={o || "all"} value={o}>{o ? statusLabel(o) : "All results"}</MenuItem>
              ))}
            </TextField>
          )}
          {section === "evidence" && (
            <TextField select size="small" label="Mode" value={extra.mode ?? ""} sx={{ minWidth: 160 }}
              onChange={(e) => setExtra({ mode: e.target.value || undefined })}>
              {["", "LIVE", "PAPER"].map((o) => <MenuItem key={o || "all"} value={o}>{o || "All modes"}</MenuItem>)}
            </TextField>
          )}
          {section === "reconciliation" && (
            <TextField select size="small" label="Severity" value={extra.severity ?? ""} sx={{ minWidth: 160 }}
              onChange={(e) => setExtra({ severity: e.target.value || undefined })}>
              {["", "CRITICAL", "WARNING", "INFO"].map((o) => <MenuItem key={o || "all"} value={o}>{o || "All"}</MenuItem>)}
            </TextField>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">{rows.length} row(s)</Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => void download()} disabled={loading}>
            Download CSV
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {active.columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={active.columns.length}>
                    <Typography variant="body2" color="text.secondary">
                      {loading ? "Loading…" : "No rows in this range — this is not a healthy result, just no data."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow key={i} hover>
                    {active.columns.map((c) => (
                      <TableCell key={c.key} sx={{ maxWidth: c.key === "message" || c.key === "summary" ? 420 : undefined }}>
                        {c.status ? <StatusChip status={r[c.key]} /> : fmt(r[c.key], c)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default ExecutionIntegrityCard;
